import { styled, css } from 'styled-components'

import { surfaceBright, variables, outline, surfaceDim, surfaceBrightPressed } from 'core/styles'

export const StyledTableWrapper = styled.div<{
  $bordered?: boolean
}>`
  box-sizing: border-box;
  position: relative;
  background: ${surfaceBright};
  width: 100%;
  max-width: 100%;
  overflow: auto;
  ${(p) =>
    p.$bordered &&
    css`
      border: 1px solid ${outline};
      border-radius: 8px;
    `}
`

export const StyledTable = styled.table<{
  $hasPinnedColumns?: boolean
}>`
  box-sizing: border-box;
  width: 100%;
  border-collapse: collapse;
`

export const StyledTableCell = styled.td<{
  $paddingTop?: string
  $paddingBottom?: string
  $textAlign?: 'left' | 'right' | 'center'
  $pinned?: boolean
  $left?: number
  $isLastPinned?: boolean
  $width?: string
}>`
  box-sizing: border-box;
  position: relative;
  padding: ${(p) => p.$paddingTop || '24px'} 24px ${(p) => p.$paddingBottom || '24px'};
  width: ${(props) => props.$width || 'auto'};
  vertical-align: center;
  text-align: ${(p) => p.$textAlign || 'left'};

  &:first-child {
    border-left: none;
  }

  ${(props) =>
    props.$pinned &&
    css`
      position: sticky;
      left: ${props.$left}px;
      z-index: 1;
      background-color: ${surfaceBright};
    `}

  ${(props) =>
    props.$isLastPinned &&
    css`
      &::after {
        position: absolute;
        top: 0;
        right: 0;
        background-color: ${outline};
        width: 2px;
        height: 100%;
        content: '';
      }
    `}
`

export const StyledTableRow = styled.tr<{
  $bordered: boolean
  $summary?: boolean
  $highlight?: boolean
  $selected?: boolean
}>`
  ${(p) => p.$bordered && `border-bottom: 1px solid ${outline};`}
  ${(p) => p.$selected && `background: ${variables.colorBlack10};`}
  ${(p) => p.$summary && 'font-weight: 600;'}

  &:last-child {
    border-bottom: none;
  }

  ${(p) =>
    p.$highlight &&
    css`
      &:hover td {
        background-color: ${variables.colorBlack10};
        cursor: pointer;
      }
    `}
`

export const StyledTableHeaderRow = styled.tr`
  border-bottom: 1px solid ${outline};
`

export const StyledTableHeaderCell = styled.th<{
  onClick: unknown
  $sorted: boolean
  $pinned?: boolean
  $left?: number
  $isLastPinned?: boolean
  $width?: string
  $align?: 'left' | 'right' | 'center'
}>`
  box-sizing: border-box;
  position: relative;
  border-bottom: 1px solid ${outline};
  background: ${surfaceDim};
  padding: 8px 24px;
  width: ${(props) => props.$width || 'auto'};
  text-align: ${(props) => props.$align || 'left'};
  color: ${variables.colorBlack90};
  font-weight: 500;

  &:first-child {
    border-left: none;
  }

  ${(props) =>
    props.$pinned &&
    css`
      position: sticky;
      left: ${props.$left}px;
      z-index: 2;
    `}

  ${(p) =>
    !!p.onClick &&
    `&:hover {
      background-color: ${surfaceBrightPressed};
      cursor: pointer;
    }`}

  ${(props) =>
    props.$sorted &&
    css`
      font-weight: 600;
    `}

  ${(props) =>
    props.$isLastPinned &&
    css`
      &::after {
        position: absolute;
        top: 0;
        right: 0;
        background-color: ${outline};
        width: 2px;
        height: 100%;
        content: '';
      }
    `}
`

export const StyledTableHeaderContent = styled.span`
  display: flex;
  align-items: center;
  white-space: nowrap;
`

export const StyledTableButtonWrapper = styled.span<{ $justifyContent?: string }>`
  display: flex;
  justify-content: ${(p) => p.$justifyContent ?? 'flex-end'};
  white-space: nowrap;
`

export const StyledTableValue = styled.span<{
  $deemphasized: boolean
  $textAlign: 'left' | 'center' | 'right'
}>`
  display: block;
  text-align: ${(p) => p.$textAlign};
  white-space: nowrap;
  ${(p) => p.$deemphasized && `color: ${variables.colorBlack50};`};
`
